import { useState, useCallback } from 'react';
import API from '../services/api';
import toast from '../utils/toast';

/**
 * Dormitory data hook — read-only projection of the Dormitory Portal.
 *
 * The Dormitory Portal is the source of truth for dormitory locations,
 * occupancy, rent, and worker assignments. The HR Portal calls its
 * `labour-cost` API and serves slim, page-shaped projections via
 * `/api/v1/dormitory-portal/overview/` and `/api/v1/dormitory-portal/residents/`.
 *
 * Both endpoints always return 200 with `available: false` and an `error`
 * string when the portal is unreachable, so callers do NOT surface a toast
 * for connectivity issues — the page renders an inline "unavailable"
 * banner instead. A thrown error here means a real client-side / auth
 * failure worth toasting.
 */
export const useDormitoryData = () => {
  const [portalOverview, setPortalOverview] = useState(null);
  const [portalResidents, setPortalResidents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPortalOverview = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.getDormitoryPortalOverview(params);
      setPortalOverview(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error
        || err.response?.data?.message
        || 'Failed to fetch dormitory portal overview';
      setError(errorMsg);
      toast.error(errorMsg);
      setPortalOverview({
        available: false,
        error: errorMsg,
        summary: null,
        cost_split: null,
        dorms: [],
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPortalResidents = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await API.getDormitoryPortalResidents(params);
      setPortalResidents(response.data);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error
        || err.response?.data?.message
        || 'Failed to fetch dormitory portal residents';
      setError(errorMsg);
      toast.error(errorMsg);
      setPortalResidents({
        available: false,
        error: errorMsg,
        residents: [],
        stats: null,
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    portalOverview,
    portalResidents,
    loading,
    error,
    fetchPortalOverview,
    fetchPortalResidents,
  };
};

export default useDormitoryData;
